/**
 * Dispatch — lista de broadcasts + criar novo.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus, Radio } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { dispatchApi, jobsApi, type Broadcast, type BroadcastStatus } from "@/lib/api-jobs";
import { formatRelative } from "@/lib/format";

const STATUS_VARIANT: Record<BroadcastStatus, "warning" | "info" | "success" | "danger" | "muted"> = {
  pending: "warning",
  dispatching: "info",
  completed: "success",
  exhausted: "danger",
  cancelled: "muted",
};

const STATUS_LABEL: Record<BroadcastStatus, string> = {
  pending: "Pendente",
  dispatching: "Distribuindo",
  completed: "Concluído",
  exhausted: "Esgotado",
  cancelled: "Cancelado",
};

export function DispatchPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [jobId, setJobId] = useState("");
  const [radius, setRadius] = useState("30");
  const [expires, setExpires] = useState("60");

  const list = useQuery({ queryKey: ["broadcasts"], queryFn: () => dispatchApi.listBroadcasts({ size: 200 }) });
  const openJobs = useQuery({
    queryKey: ["jobs-open"],
    queryFn: () => jobsApi.list({ size: 200, status_filter: "open" }),
  });

  const create = useMutation({
    mutationFn: () =>
      dispatchApi.createBroadcast({
        job_id: jobId,
        radius_km: Number(radius),
        expires_in_minutes: Number(expires),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      toast.success("Broadcast disparado");
      setOpen(false);
      setJobId("");
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const columns: ColumnDef<Broadcast>[] = [
    {
      id: "job_id",
      accessorKey: "job_id",
      header: "Chamado",
      cell: ({ row }) => (
        <Link to={`/jobs/${row.original.job_id}`} className="text-primary-700 dark:text-primary-300 font-mono text-xs hover:underline">
          {row.original.job_id.slice(0, 8)}…
        </Link>
      ),
    },
    {
      id: "batch_number",
      accessorKey: "batch_number",
      header: "Tentativa",
      cell: ({ row }) => <Badge variant="default">#{row.original.batch_number}</Badge>,
    },
    {
      id: "radius_km",
      accessorKey: "radius_km",
      header: "Raio",
      cell: ({ row }) => `${row.original.radius_km} km`,
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={STATUS_VARIANT[row.original.status]} dot>
          {STATUS_LABEL[row.original.status]}
        </Badge>
      ),
    },
    {
      id: "started_at",
      accessorKey: "started_at",
      header: "Iniciado",
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{formatRelative(row.original.started_at)}</span>
      ),
    },
    {
      id: "expires_at",
      accessorKey: "expires_at",
      header: "Expira",
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{formatRelative(row.original.expires_at)}</span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Dispatch"
        description="Broadcasts pra técnicos via match engine (score por geo + skill + rank)."
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Novo broadcast
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        searchPlaceholder="Buscar broadcast..."
        emptyTitle="Nenhum broadcast"
        emptyDescription="Crie um broadcast pra ofertar um chamado pros técnicos no raio."
        emptyAction={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Novo broadcast
          </Button>
        }
        onRowClick={(b) => navigate(`/dispatch/${b.id}`)}
        columnLabels={{
          job_id: "Chamado",
          batch_number: "Tentativa",
          radius_km: "Raio",
          status: "Status",
          started_at: "Iniciado",
          expires_at: "Expira",
        }}
      />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Novo broadcast</SheetTitle>
            <SheetDescription>
              <Radio className="h-4 w-4 inline -mt-0.5 mr-1" />
              Match engine gera shortlist e (TODO) envia WhatsApp pros técnicos.
            </SheetDescription>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Chamado *</Label>
              <Select value={jobId} onValueChange={setJobId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um chamado em status OPEN" />
                </SelectTrigger>
                <SelectContent>
                  {openJobs.data?.items.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      <span className="font-mono text-xs mr-2">{j.code}</span>
                      {j.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Raio (km)</Label>
                <Input type="number" value={radius} onChange={(e) => setRadius(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Expira em (min)</Label>
                <Input type="number" value={expires} onChange={(e) => setExpires(e.target.value)} />
              </div>
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="outline">Cancelar</Button>
            </SheetClose>
            <Button onClick={() => create.mutate()} disabled={!jobId || create.isPending}>
              {create.isPending ? "Disparando..." : "Disparar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
